import {hideableValueCreateHidden, hideableValueModifyValue} from "@wix/devzai-utils-common";
import type {CurrencyCode, OmitStrict, RichTextContent, HideableValue} from '@wix/devzai-utils-common';
import type {DoppeFormActionSettings} from './doppe-form-action-settings';
import {
    doppeFormActionSettingsResolveDefaultCrmType,
    getDefaultDoppeFormActionSettings
} from './doppe-form-action-settings';
import type {DoppeDtoAccountData} from './doppe-dto-account-data';
import type {DoppeCheckoutProvider} from './doppe-checkout-provider';
import {DoppeCheckoutProvider as DoppeCheckoutProviderValues} from './doppe-checkout-provider';

// never change this value, it is used to identify the default currency code
export const DefaultCurrencyCode = 'USD';

export interface DoppeCheckoutSettings extends DoppeFormActionSettings {
    // Checkout-specific UI fields
    showEmailInput: boolean;
    showAddressInput: boolean;
    showPhoneInput: boolean;
    showConsent: boolean;

    // Checkout-specific notification fields
    emailConfirmationMessage: HideableValue<RichTextContent>;
    customConfirmationMail: HideableValue<string>;
    confirmationPhone: HideableValue<string>;

    // Checkout-specific features
    sendReceipt: boolean;
    currencyCode: CurrencyCode;

    // Note: CRM fields (crmType, creatorsListId, linkedWixSiteListId, listId) 
    // and common form fields (showConsentCheckbox, customConsentText, etc.) 
    // are inherited from DoppeFormActionSettings
}

export interface DoppeCheckoutSettingsWithBindings extends OmitStrict<DoppeCheckoutSettings, 'currencyCode'> {
    title: HideableValue<string>;
}

/**
 * Discriminated union for checkout settings.
 * - Stripe: Contains full checkout form settings (email, address, CRM, etc.)
 * - WixEcom: Empty settings (Wix checkout handles everything on their side)
 */
export type DoppeCheckoutActionCheckoutSettings =
    | DoppeCheckoutActionCheckoutSettings.Stripe
    | DoppeCheckoutActionCheckoutSettings.WixEcom
;

export namespace DoppeCheckoutActionCheckoutSettings {
    interface Base<KIND extends DoppeCheckoutProvider> {
        kind: KIND;
    }

    export interface Stripe extends Base<typeof DoppeCheckoutProviderValues.Stripe> {
        settings: DoppeCheckoutSettingsWithBindings;
    }

    export interface WixEcom extends Base<typeof DoppeCheckoutProviderValues.WixEcom> {
        settings: {};
    }
}

export interface DoppeCheckoutActionSettings {
    checkoutSettings: DoppeCheckoutActionCheckoutSettings;
}

export function doppeCheckoutActionCheckoutSettingsCreateStripe(
    settings: DoppeCheckoutSettingsWithBindings
): DoppeCheckoutActionCheckoutSettings.Stripe {
    return {
        kind: DoppeCheckoutProviderValues.Stripe,
        settings: settings,
    };
}

export function doppeCheckoutActionCheckoutSettingsCreateWixEcom(): DoppeCheckoutActionCheckoutSettings.WixEcom {
    return {
        kind: DoppeCheckoutProviderValues.WixEcom,
        settings: {},
    };
}

export function doppeCheckoutActionCheckoutSettingsIsStripe(
    checkoutSettings: DoppeCheckoutActionCheckoutSettings
): checkoutSettings is DoppeCheckoutActionCheckoutSettings.Stripe {
    return checkoutSettings.kind === DoppeCheckoutProviderValues.Stripe;
}

export function doppeCheckoutActionCheckoutSettingsIsWixEcom(
    checkoutSettings: DoppeCheckoutActionCheckoutSettings
): checkoutSettings is DoppeCheckoutActionCheckoutSettings.WixEcom {
    return checkoutSettings.kind === DoppeCheckoutProviderValues.WixEcom;
}

export function doppeCheckoutActionCheckoutSettingsResolveForAction(
    checkoutSettings: DoppeCheckoutActionCheckoutSettings | undefined,
): DoppeCheckoutActionCheckoutSettings {
    if (!checkoutSettings || !('kind' in checkoutSettings)) {
        return doppeCheckoutActionCheckoutSettingsCreateWixEcom();
    }

    switch (checkoutSettings.kind) {
        case DoppeCheckoutProviderValues.Stripe:
            return doppeCheckoutActionCheckoutSettingsCreateStripe(
                getDefaultDoppeCheckoutSettingsForAction(checkoutSettings.settings)
            );
        case DoppeCheckoutProviderValues.WixEcom:
            return doppeCheckoutActionCheckoutSettingsCreateWixEcom();
    }
}

export function getDefaultDoppeCheckoutActionSettings(overrides?: Partial<DoppeCheckoutSettings>) {
    return {
        checkoutSettings: doppeCheckoutActionCheckoutSettingsCreateStripe(
            getDefaultDoppeCheckoutSettingsForAction(overrides)
        ),
    } satisfies DoppeCheckoutActionSettings;
}

export function getDefaultDoppeCheckoutActionSettingsWithCrmType(
    currentAccount: DoppeDtoAccountData.WithExtendedData | undefined,
    overrides?: Partial<DoppeCheckoutSettings>
) {
    const defaultCrmType = doppeFormActionSettingsResolveDefaultCrmType(currentAccount);

    return {
        checkoutSettings: doppeCheckoutActionCheckoutSettingsCreateStripe(
            getDefaultDoppeCheckoutSettingsForAction({
                crmType: defaultCrmType,
                ...overrides
            })
        ),
    } satisfies DoppeCheckoutActionSettings;
}

/**
 * Get default checkout action settings with the checkout provider determined by account settings.
 * New users with Wix BM integration will use WixEcom, others will use Stripe.
 */
export function getDefaultDoppeCheckoutActionSettingsWithProvider(
    options: {
        currentAccount: DoppeDtoAccountData.WithExtendedData | undefined;
        useWixCheckout: boolean;
        checkoutSettingsOverrides?: Partial<DoppeCheckoutSettings>;
    }
) {
    const {
        currentAccount,
        useWixCheckout,
        checkoutSettingsOverrides,
    } = options;

    if (useWixCheckout) {
        return {
            checkoutSettings: doppeCheckoutActionCheckoutSettingsCreateWixEcom(),
        } satisfies DoppeCheckoutActionSettings;
    }

    const defaultCrmType = doppeFormActionSettingsResolveDefaultCrmType(currentAccount);

    return {
        checkoutSettings: doppeCheckoutActionCheckoutSettingsCreateStripe(
            getDefaultDoppeCheckoutSettingsForAction({
                crmType: defaultCrmType,
                ...checkoutSettingsOverrides
            })
        ),
    } satisfies DoppeCheckoutActionSettings;
}

export function doppeCheckoutActionSettingsCalculateFromAccountSettings(
    checkoutSettings: DoppeCheckoutSettingsWithBindings,
    currencyCode: CurrencyCode
) {
    return {
        ...checkoutSettings,
        currencyCode: currencyCode
    } satisfies DoppeCheckoutSettings;
}

export function getDefaultDoppeCheckoutSettingsForAction(overrides?: Partial<DoppeCheckoutSettingsWithBindings>) {

    const {
        currencyCode,
        ...checkoutSettings
    } = doppeCheckoutActionSettingsGetDefaultValues(overrides)

    return {
        ...checkoutSettings,
        title: hideableValueCreateHidden(''),

        ...overrides,
    } satisfies DoppeCheckoutSettingsWithBindings;
}

export function doppeCheckoutActionSettingsGetDefaultValues(overrides?: Partial<DoppeCheckoutSettings>) {
    return {
        // Include all form action defaults (CRM fields + common form fields)
        ...getDefaultDoppeFormActionSettings(),

        // Checkout-specific UI defaults
        showEmailInput: true,
        showAddressInput: false,
        showPhoneInput: true,
        showConsent: true,

        // Checkout-specific notification defaults
        sendReceipt: true,
        customConfirmationMail: hideableValueCreateHidden(''),
        confirmationPhone: hideableValueCreateHidden(''),
        emailConfirmationMessage: hideableValueCreateHidden(''),

        // Checkout-specific feature defaults
        currencyCode: DefaultCurrencyCode,

        // Apply any overrides
        ...overrides,
    } satisfies DoppeCheckoutSettings;
}


export function prepareCheckoutSettingsForViewer(settings: DoppeCheckoutActionSettings) {
    if (doppeCheckoutActionCheckoutSettingsIsStripe(settings.checkoutSettings)) {
        hideableValueModifyValue(settings.checkoutSettings.settings.customEmailForNotifications, '')
        hideableValueModifyValue(settings.checkoutSettings.settings.customConfirmationMail, '')
    }
}

